import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import {
  DataFieldInputs,
  LINEAR_ACCEPTABLE,
  LINEAR_UNACCEPTABLE,
  isAllowedNumericInput,
} from '@/features/non-compliance-actions'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { Alert, AlertDescription } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { DateTimePickerInput } from '@/shared/ui/datetime-picker-input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import { supplyDisplayName } from '@/shared/lib/supply-label'
import {
  formatSiteDateTime,
  formatTenantDateTimeWithZone,
  nowInTimeZone,
  siteZoneAbbreviation,
  toBrowserWallClockString,
  toDatetimeLocalValue,
  toWallClockString,
  wallClockToUtcIso,
} from '@/shared/lib/tenantDateTime'
import { LotReadOnlyRow, type LotRow } from './LotReadOnlyRow'
import { AddLoggerInline, type CreatedLogger } from './AddLoggerInline'
import { richTextToPlainText } from '@/shared/lib/rich-text-html'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import type { CurrentUserInfo } from '@/shared/types/CurrentUserInfo'
import { TaskStatusBadge, TASK_STATUS_ZONE_COLOR } from '@/features/task-status'

export interface ExistingTaskLog {
  id: number
  comments: string | null
  completedAt: string | null
  loggedAt: string | null
  labLoggerId: number | null
  originalCompletedByLabel: string | null
  dataFieldLines: {
    instrumentTaskDataFieldId: number | null
    value: string | null
    acceptableValue: boolean | null
  }[]
  lotLines: { lotId: number | null; lotDetailId: number | null }[]
  dataFieldAttachments?: {
    instrumentTaskDataFieldId: number | null
    id: number
    attachmentName: string | null
    downloadHref: string | null
  }[]
}

export interface DataFieldFilePayload {
  attachmentName: string
  mimeType: string
  dataBase64: string
}

export type LotOptionRow = {
  lotId: number
  label: string | null
  lotDetails: { id: number; label: string }[]
}

export type LoggerOption = {
  id: number
  label: string
  /**
   * REBUILD-531: present on the prepended logged-in-user row (the open-logging default
   * performer). A standalone logger row has userId null/absent. id === 0 marks the current
   * user row (legacy convention: loggerId 0 = "the logged-in user, not a logger").
   */
  userId?: number | null
}

export type ComplianceWindow = {
  intervalHours: number | null
  lateTimeIntervalHours: number | null
  leadTimeIntervalHours: number | null
  isFixedSchedule: boolean
  lastCompletedAt: string | null
  taskStatusCode: string | null
  /** Authoritative window boundaries from the backend status engine (site-local wall clock). */
  nextOccurrence: string | null
  windowEnd: string | null
  leadDate: string | null
}

export type { LotRow }

export type LotValidityWarning = {
  lotName: string
  reason:
    | 'expired'
    | 'retired'
    | 'discarded'
    | 'data_fields_missing'
    | 'lot_not_specified'
    | 'missing_selection'
}

export interface TaskLogFormProps {
  mode: 'new' | 'edit'
  dataFields: WebInstrumentsTaskLogDataField[]
  /** @deprecated Kept for the standalone Log page until it is ported to the new lot-row design. */
  lotOptions?: LotOptionRow[]
  loggerOptions: LoggerOption[]
  complianceWindow: ComplianceWindow | null
  existingTaskLog: ExistingTaskLog | null
  formAction: string
  formMethod: 'post' | 'patch'
  prefillCompletedAt?: string | null
  /**
   * UTC iso8601 overdue boundary (window_end || date) of the task's open non-compliance.
   * Used as a prefill display value; Log Task does not cap or block on this boundary
   * (REBUILD-401 Late path). Resolve paths enforce their own guard.
   */
  openMissTime?: string | null
  errors?: Record<string, string | string[]>
  /** When true, POST includes source=inline so the server redirects back to instrument show. */
  isInline?: boolean
  /** Override redirect target after inline submit (sent as return_to param). */
  returnTo?: string
  /**
   * Inertia partial-reload scope for inline submits. When set, the submit only refreshes
   * these props (e.g. ['current_schedule']) instead of doing a full-page reload.
   */
  reloadOnly?: string[]
  /** Called after a successful inline submission (before Inertia navigation). */
  onSuccess?: () => void
  /** Called when user cancels (inline mode only). */
  onCancel?: () => void
  /**
   * Invalid lots that should block Mark-as-Complete until resolved.
   * Rendered as a danger warning banner with the optional changeLotSlot.
   */
  lotValidityWarnings?: LotValidityWarning[]
  /**
   * Slot rendered inside the lot-validity warning banner when the linked lot
   * is invalid and no per-row Change slots are available (legacy warning banner CTA).
   */
  changeLotSlot?: React.ReactNode
  /**
   * The task's linked control/reagent lot (from current-schedule payload).
   * When provided, replaces the generic lot-dropdown UI with a read-only row + (Change).
   */
  linkedLot?: LotRow | null
  /**
   * Configured used/consumable lots (from current-schedule payload).
   * Each rendered as a read-only row with its own (Change) slot from lotModalSlots.
   */
  usedLots?: LotRow[]
  /**
   * Per-lot Change modal triggers keyed by lot ID.
   * Rendered next to each linked/used lot row.
   */
  lotModalSlots?: Map<number, React.ReactNode>
  /**
   * Read-only task notes, rendered between the lots and the data fields — the slot legacy
   * used for the task's Notes box (REBUILD-359).
   */
  notesSlot?: React.ReactNode
  /**
   * @deprecated Use linkedLot instead. Kept for the standalone Log page.
   */
  linkedLotId?: number | null
  /**
   * @deprecated Use linkedLot instead. Kept for the standalone Log page.
   */
  linkedLotNoInventory?: boolean
  /**
   * Tenant authentication mode: 'PR' = current user is the logger (no picklist);
   * 'OL' = Other Logger picklist. Defaults to 'OL' behaviour when absent.
   */
  logAuthenticationCode?: string | null
  /**
   * IANA timezone of the task's instrument site. When present, the submitted
   * `completed_at` wall-clock time is interpreted in this zone (DST-aware), matching
   * how the rest of the app converts site-local input to UTC. Falls back to the
   * browser's offset when absent.
   */
  siteTimeZone?: string | null
  /**
   * REBUILD-531: instrument whose effective open-logging code gates the inline "Add new user"
   * create on the server. Forwarded to the create endpoint so the Pundit policy resolves the
   * instrument override. Absent only on surfaces with no single instrument context.
   */
  instrumentId?: number | null
  /**
   * True when the completion instant being evaluated (now, when the form opens) falls on a
   * day off and the task's interval qualifies. Non-blocking — save stays available.
   */
  dayOff?: boolean
  /**
   * Schedule level that owns the weekly day off, or "System" for a custom date off.
   * Instrument | Unit | Site | System.
   */
  scheduleLevel?: string | null
}

interface TaskLogFormData {
  completedAt: string
  comments: string
  labLoggerId: string
  modReason: string
  dataFieldEntries: { instrumentTaskDataFieldId: number; value: string; linearAcceptable: string }[]
  lotEntries: { lotId: string; lotDetailId: string }[]
}

type TaskLogFormFieldErrors = Partial<{
  [K in keyof TaskLogFormData]: string | string[]
}> & { base?: string | string[]; loggedAt?: string | string[] }

interface SharedPageProps {
  currentUserInfo?: CurrentUserInfo | null
  tenantTimeFormat?: number | null
  tenantTimeZone?: string | null
  [key: string]: unknown
}

const STATUS_ZONE_FALLBACK = TASK_STATUS_ZONE_COLOR.UTD!

/** How often live "now" displays (Completed at label, timeline now-marker) refresh. */
const LIVE_CLOCK_TICK_MS = 15_000

/** Earliest edited "Completed at" accepted (REBUILD-627); mirrors InstrumentTaskLog::MIN_COMPLETED_AT. */
const MIN_COMPLETED_AT_WALL_CLOCK = '2010-01-01T00:00'

// Keep the now marker fully inside the overflow-hidden bar (left: 0%/100% clips a 4px wick).
const NOW_MARKER_MIN_PCT = 0.75
const NOW_MARKER_MAX_PCT = 99.25
// Centered labels near the card edges overflow; switch to start/end alignment (REBUILD-565).
const NOW_MARKER_LABEL_LEFT_ALIGN_MAX_PCT = 10
const NOW_MARKER_LABEL_RIGHT_ALIGN_MIN_PCT = 90

/** Horizontal translate for the now-marker date label so it stays inside the card. */
export const nowMarkerLabelTranslateClass = (leftPct: number): string => {
  if (leftPct <= NOW_MARKER_LABEL_LEFT_ALIGN_MAX_PCT) return 'translate-x-0'
  if (leftPct >= NOW_MARKER_LABEL_RIGHT_ALIGN_MIN_PCT) return '-translate-x-full'
  return '-translate-x-1/2'
}

/** Current time, refreshed every LIVE_CLOCK_TICK_MS; frozen while `isPaused`. */
const useLiveClock = (isPaused: boolean = false): Date => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (isPaused) return
    const refreshNow = () => setNow(new Date())
    // Resync immediately on resume so a long pause doesn't show a stale time for a full tick.
    const resyncTimer = setTimeout(refreshNow, 0)
    const tickTimer = setInterval(refreshNow, LIVE_CLOCK_TICK_MS)
    return () => {
      clearTimeout(resyncTimer)
      clearInterval(tickTimer)
    }
  }, [isPaused])
  return now
}

const firstError = (rawError: string | string[] | undefined): string | undefined => {
  if (rawError == null) return undefined
  return Array.isArray(rawError) ? rawError[0] : rawError
}

const formatHours = (hours: number): string => {
  const h = t('instruments.taskLogs.hourSuffix')
  const d = t('instruments.taskLogs.daySuffix')
  if (hours < 24) return `${hours}${h}`
  const days = Math.floor(hours / 24)
  const remainder = hours % 24
  return remainder > 0 ? `${days}${d} ${remainder}${h}` : `${days}${d}`
}

const formatMinutesAllowance = (minutes: number): string => {
  const m = t('instruments.taskLogs.minuteSuffix')
  const h = t('instruments.taskLogs.hourSuffix')
  const d = t('instruments.taskLogs.daySuffix')
  if (minutes < 60) return `${minutes}${m}`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}${h}`
  const days = Math.floor(hours / 24)
  const remainder = hours % 24
  return remainder > 0 ? `${days}${d} ${remainder}${h}` : `${days}${d}`
}

export const ComplianceTimeline = ({
  window: complianceWindow,
  siteTimeZone,
}: {
  window: ComplianceWindow
  siteTimeZone?: string | null
}) => {
  const { tenantTimeFormat } = usePage<SharedPageProps>().props
  const now = useLiveClock()
  const cw = complianceWindow

  const statusCode = cw.taskStatusCode ?? ''

  if (!cw.lastCompletedAt) {
    return (
      <div className="rounded-lg border p-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">{t('instruments.taskLogs.complianceWindow')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('instruments.taskLogs.noPreviousLog')}
            {cw.intervalHours ? (
              <>
                {' · '}
                {t('instruments.taskLogs.intervalLabel')} {cw.intervalHours}
                {t('instruments.taskLogs.hourSuffix')}
              </>
            ) : null}
          </p>
        </div>
        {statusCode && <TaskStatusBadge taskStatusCode={statusCode} />}
      </div>
    )
  }

  // Boundaries come from the backend status engine — same computation that produced
  // taskStatusCode — so the bar segments, marker, and badge always agree.
  // No frontend catch-up: the engine anchors on the first occurrence after last
  // completion (legacy parity) and never walks forward to "today's" cycle.
  const parseWallClock = (wallClock: string | null): Date | null => {
    if (!wallClock) return null
    return siteTimeZone
      ? new Date(wallClockToUtcIso(wallClock.slice(0, 16), siteTimeZone))
      : new Date(wallClock)
  }

  const nextDue = parseWallClock(cw.nextOccurrence)
  const overdueAt = parseWallClock(cw.windowEnd)
  const leadStartRaw = parseWallClock(cw.leadDate)

  // Fall back gracefully when boundary fields are absent (older payloads).
  if (!nextDue || !overdueAt) return null

  // Legacy parity (timeline.component.ts): zones are FIXED-WIDTH columns, NOT proportional to
  // real time. Timed schedules draw 3 equal zones (Before / Due Now / Overdue = 33.3% each);
  // calendar/fixed schedules draw 2 equal zones (Due Now / Overdue = 49.9% each) with no lead
  // zone. The now-marker is placed at its phase's fixed start plus (elapsed / total) of that
  // phase's width, advancing with the live clock.
  const hasLeadZone =
    !cw.isFixedSchedule && leadStartRaw != null && leadStartRaw.getTime() < nextDue.getTime()
  const leadStart = hasLeadZone ? leadStartRaw! : nextDue

  const isOverdue = now >= overdueAt

  // Fixed zone widths + boundaries (percent). Timed: 0–33.3 Before, 33.3–66.6 Due Now,
  // 66.6–100 Overdue. Calendar: 0–49.9 Due Now, 49.9–100 Overdue.
  const zoneWidth = hasLeadZone ? 33.3 : 49.9
  const leadStartPct = 0
  const duePct = hasLeadZone ? zoneWidth : 0
  const overduePct = duePct + zoneWidth

  // Marker position mirrors legacy getMarkerPosition: fixed zone-start + elapsed/total × width,
  // capped at 99. Hidden (null) for UTD and any non-live phase — legacy sets markerPosition = -1.
  const elapsedFraction = (from: Date, to: Date): number => {
    const total = to.getTime() - from.getTime()
    if (total <= 0) return 0
    return Math.max(0, Math.min(1, (now.getTime() - from.getTime()) / total))
  }

  let nowMarkerPct: number | null = null
  if (statusCode === 'RDY' && hasLeadZone) {
    nowMarkerPct = elapsedFraction(leadStart, nextDue) * zoneWidth
  } else if (statusCode === 'DNW') {
    nowMarkerPct = duePct + elapsedFraction(nextDue, overdueAt) * zoneWidth
  } else if (statusCode === 'ODE') {
    // Overdue grows past window_end over the late window; cap the wick at 99 like legacy.
    const lateMs =
      (cw.lateTimeIntervalHours ?? 0) * 60_000 || overdueAt.getTime() - nextDue.getTime()
    const overdueElapsed = lateMs > 0 ? (now.getTime() - overdueAt.getTime()) / lateMs : 1
    nowMarkerPct = Math.min(99, overduePct + Math.max(0, overdueElapsed) * zoneWidth)
  }
  // UTD (and any other state) → marker hidden.

  const leadZone = TASK_STATUS_ZONE_COLOR.RDY!
  const dueZone = TASK_STATUS_ZONE_COLOR.DNW!
  const overdueZone = TASK_STATUS_ZONE_COLOR.ODE!
  const markerColor = (TASK_STATUS_ZONE_COLOR[statusCode] ?? STATUS_ZONE_FALLBACK).marker
  const markerTextColor = (TASK_STATUS_ZONE_COLOR[statusCode] ?? STATUS_ZONE_FALLBACK).text

  // Legacy parity (timeline.template.html): the marker carries the site-local date+time of
  // "now", advancing with the live clock. Site zone when known, else the browser clock.
  const nowMarkerLabel =
    nowMarkerPct === null
      ? null
      : formatSiteDateTime(
          siteTimeZone ? toWallClockString(now, siteTimeZone) : toBrowserWallClockString(now),
          siteTimeZone,
          tenantTimeFormat,
        )
  const nowMarkerLeftPct = Math.min(
    Math.max(nowMarkerPct ?? 0, NOW_MARKER_MIN_PCT),
    NOW_MARKER_MAX_PCT,
  )

  const leadLabel = `${formatMinutesAllowance(cw.leadTimeIntervalHours ?? 0)} ${t('instruments.taskLogs.phaseBeforeSuffix')}`

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{t('instruments.taskLogs.complianceWindow')}</span>
        <span className="text-xs text-muted-foreground">
          {t('instruments.taskLogs.lastPerformed')}{' '}
          {formatSiteDateTime(cw.lastCompletedAt, siteTimeZone, tenantTimeFormat)}
        </span>
      </div>
      {nowMarkerLabel !== null && (
        <div className="relative h-4">
          <span
            className={cn(
              'absolute whitespace-nowrap text-[10px] font-medium',
              nowMarkerLabelTranslateClass(nowMarkerLeftPct),
              markerTextColor,
            )}
            style={{ left: `${nowMarkerLeftPct}%` }}
          >
            {nowMarkerLabel}
          </span>
        </div>
      )}
      <div
        className="relative h-6 rounded-full bg-muted overflow-hidden"
        role="img"
        aria-label={t('instruments.taskLogs.complianceWindow')}
      >
        {hasLeadZone && (
          <div
            className={`absolute inset-y-0 ${leadZone.segment} flex items-center justify-center`}
            style={{ left: `${leadStartPct}%`, width: `${duePct - leadStartPct}%` }}
            title={t('instruments.taskLogs.phaseLeadTimeTooltip', {
              allowance: formatMinutesAllowance(cw.leadTimeIntervalHours ?? 0),
            })}
          >
            {duePct - leadStartPct > 12 && (
              <span className={`text-[10px] font-medium ${leadZone.text} truncate px-1`}>
                {leadLabel}
              </span>
            )}
          </div>
        )}
        {overduePct > duePct && (
          <div
            className={`absolute inset-y-0 ${dueZone.segment} flex items-center justify-center`}
            style={{ left: `${duePct}%`, width: `${overduePct - duePct}%` }}
            title={t('instruments.taskLogs.phaseDueNowTooltip')}
          >
            {overduePct - duePct > 12 && (
              <span className={`text-[10px] font-medium ${dueZone.text} truncate px-1`}>
                {t('instruments.taskLogs.phaseDueNow')}
              </span>
            )}
          </div>
        )}
        {overduePct < 100 && (
          <div
            className={`absolute inset-y-0 ${overdueZone.segment} flex items-center justify-center`}
            style={{ left: `${overduePct}%`, width: `${100 - overduePct}%` }}
            title={t('instruments.taskLogs.phaseOverdueTooltip', {
              allowance: formatMinutesAllowance(
                cw.lateTimeIntervalHours ??
                  Math.round((overdueAt.getTime() - nextDue.getTime()) / 60_000),
              ),
            })}
          >
            {100 - overduePct > 12 && (
              <span className={`text-[10px] font-medium ${overdueZone.text} truncate px-1`}>
                {t('instruments.taskLogs.phaseOverdue')}
              </span>
            )}
          </div>
        )}
        {nowMarkerPct !== null && (
          <div
            className={`absolute inset-y-0 w-1 -translate-x-1/2 rounded-full ${markerColor}`}
            style={{ left: `${nowMarkerLeftPct}%` }}
            aria-hidden
          />
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {statusCode ? <TaskStatusBadge taskStatusCode={statusCode} /> : <span />}
        <span>
          {statusCode === 'UTD'
            ? t('instruments.taskLogs.upToDatePlaceholder')
            : isOverdue
              ? `${t('instruments.taskLogs.overdueBy')} ${formatHours(Math.round((now.getTime() - overdueAt.getTime()) / 3_600_000))}`
              : `${t('instruments.taskLogs.dueByPrefix')} ${formatSiteDateTime(cw.windowEnd!, siteTimeZone, tenantTimeFormat)}`}
        </span>
      </div>
    </div>
  )
}

/**
 * Whether the form renders read-only lot rows (linked/used lots from the current-schedule
 * payload) instead of the legacy editable dropdown. In this mode the submitted lot entries
 * are a pure projection of the current prop selections, not user-editable form state.
 */
export const usesReadOnlyLotRows = (
  props: Pick<TaskLogFormProps, 'linkedLot' | 'usedLots'>,
): boolean => props.linkedLot != null || (props.usedLots ?? []).length > 0

/**
 * Lot entries derived from the current linked/used lot selections. Each row that has a
 * persisted currentLotDetailId is submitted. Recomputed from live props at submit time so a
 * supply selected via the Change modal AFTER the form mounted is not lost to a stale mount
 * snapshot (REBUILD-111).
 */
export const deriveReadOnlyLotEntries = (
  props: Pick<TaskLogFormProps, 'linkedLot' | 'usedLots'>,
): { lotId: string; lotDetailId: string }[] => {
  const rows: { lotId: string; lotDetailId: string }[] = []
  if (props.linkedLot?.currentLotDetailId != null) {
    rows.push({
      lotId: String(props.linkedLot.id),
      lotDetailId: String(props.linkedLot.currentLotDetailId),
    })
  }
  for (const usedLot of props.usedLots ?? []) {
    if (usedLot.currentLotDetailId != null) {
      rows.push({ lotId: String(usedLot.id), lotDetailId: String(usedLot.currentLotDetailId) })
    }
  }
  return rows
}

const buildInitialForm = (props: TaskLogFormProps): TaskLogFormData => {
  const existing = props.existingTaskLog
  const siteTimeZone = props.siteTimeZone
  const now = siteTimeZone
    ? toWallClockString(new Date(), siteTimeZone)
    : toBrowserWallClockString()
  const dataFields = props.dataFields ?? []

  const dataFieldEntries = dataFields.map((dataField) => {
    const line = existing?.dataFieldLines?.find(
      (fieldLine) => fieldLine.instrumentTaskDataFieldId === dataField.id,
    )
    const linearAcceptable =
      dataField.dataFieldType === 'linear' && line
        ? line.acceptableValue
          ? LINEAR_ACCEPTABLE
          : LINEAR_UNACCEPTABLE
        : ''
    return {
      instrumentTaskDataFieldId: dataField.id,
      value: line?.value ?? '',
      linearAcceptable,
    }
  })

  let lotEntries: { lotId: string; lotDetailId: string }[]
  if (existing?.lotLines?.length) {
    lotEntries = existing.lotLines.map((lotLine) => ({
      lotId: lotLine.lotId != null ? String(lotLine.lotId) : '',
      lotDetailId: lotLine.lotDetailId != null ? String(lotLine.lotDetailId) : '',
    }))
  } else if (usesReadOnlyLotRows(props)) {
    // New design: derive lot entries from the current persistent selections.
    // Each row that has a currentLotDetailId is included in the snapshot at log time.
    lotEntries = deriveReadOnlyLotEntries(props)
  } else if (props.mode === 'new' && props.linkedLotId != null) {
    // Legacy path: standalone Log page still passes linkedLotId + lotOptions.
    const linkedOption = (props.lotOptions ?? []).find(
      (lotOption) => lotOption.lotId === props.linkedLotId,
    )
    const autoDetailId =
      linkedOption?.lotDetails.length === 1 ? String(linkedOption.lotDetails[0].id) : ''
    lotEntries = [{ lotId: String(props.linkedLotId), lotDetailId: autoDetailId }]
  } else {
    lotEntries = [{ lotId: '', lotDetailId: '' }]
  }

  // prefillCompletedAt is now a site-local wall-clock string (localized server-side in
  // new_log_prefill_completed_at, byte-identical to the dashboard Resolve modal's missedTime),
  // just like `existing.completedAt`. toDatetimeLocalValue only trims it to minutes — there is
  // NO UTC->site-zone conversion on the frontend (per .cursor/rules/frontend-datetime-agent.mdc).
  const prefillCompletedAt =
    !existing && props.prefillCompletedAt ? toDatetimeLocalValue(props.prefillCompletedAt) : null

  // REBUILD-531: open-logging default performer. For a NEW log in OL mode (loggerOptions carry
  // a prepended current-user row with a userId), preselect that row so Completed By defaults to
  // the logged-in user. Editing keeps the persisted logger; PR mode has no picklist.
  const defaultPerformerOption = (props.loggerOptions ?? []).find((option) => option.userId != null)
  const defaultLabLoggerId =
    props.logAuthenticationCode === 'OL' && defaultPerformerOption
      ? String(defaultPerformerOption.id)
      : ''

  return {
    completedAt: existing?.completedAt
      ? existing.completedAt.slice(0)
      : (prefillCompletedAt ?? now),
    comments: existing?.comments ?? '',
    labLoggerId: existing?.labLoggerId != null ? String(existing.labLoggerId) : defaultLabLoggerId,
    modReason: '',
    dataFieldEntries,
    lotEntries,
  }
}

const lotWarningLabel = (warning: LotValidityWarning): string => {
  if (warning.reason === 'lot_not_specified' || warning.reason === 'missing_selection')
    return t('instruments.taskLogs.lotNotSpecifiedWarning').replace(
      '{{lotName}}',
      supplyDisplayName(warning.lotName),
    )
  if (warning.reason === 'expired')
    return t('instruments.taskLogs.lotExpiredWarning').replace('{{lotName}}', warning.lotName)
  if (warning.reason === 'retired')
    return t('instruments.taskLogs.lotRetiredWarning').replace('{{lotName}}', warning.lotName)
  if (warning.reason === 'data_fields_missing')
    return t('instruments.taskLogs.lotDataFieldsMissingWarning').replace(
      '{{lotName}}',
      warning.lotName,
    )
  return t('instruments.taskLogs.lotDiscardedWarning').replace('{{lotName}}', warning.lotName)
}

type ConfirmDialogState =
  | { open: false }
  | { open: true; type: 'late' | 'before_last_performed'; message: string }

const TaskLogForm = (props: TaskLogFormProps) => {
  const {
    mode,
    dataFields,
    lotOptions,
    loggerOptions,
    complianceWindow,
    formAction,
    formMethod,
    errors: pageErrors,
    isInline = false,
    returnTo,
    reloadOnly,
    onSuccess,
    onCancel,
    lotValidityWarnings = [],
    changeLotSlot,
    linkedLot,
    usedLots = [],
    lotModalSlots,
    notesSlot,
    linkedLotNoInventory = false,
    logAuthenticationCode,
  } = props

  const { currentUserInfo, tenantTimeFormat } = usePage<SharedPageProps>().props
  const isPrMode = logAuthenticationCode === 'PR'

  const canCreateTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.CREATE)
  const canUpdateTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.UPDATE)
  const canSubmitTaskLog = mode === 'new' ? canCreateTaskLog : canUpdateTaskLog

  // Snapshot initial form values once on mount — subsequent prop changes are intentionally ignored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialData = useMemo(() => buildInitialForm(props), [])

  const form = useForm<TaskLogFormData>(initialData)

  const [dataFieldFiles, setDataFieldFiles] = useState<Record<number, DataFieldFilePayload | null>>(
    {},
  )
  const [removedAttachmentFieldIds, setRemovedAttachmentFieldIds] = useState<number[]>([])
  const [dataFieldsValid, setDataFieldsValid] = useState(true)
  const [commentRequired, setCommentRequired] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  // Which submit button started the in-flight request — only that button shows its busy label.
  const [submittingAsNpt, setSubmittingAsNpt] = useState(false)
  const attachmentStateRef = useRef({ dataFieldFiles, removedAttachmentFieldIds })
  attachmentStateRef.current = { dataFieldFiles, removedAttachmentFieldIds }

  // Pending submit flags — set before calling executeSubmit so the transform picks them up.
  const pendingIsNptRef = useRef(false)
  const pendingEnteredLateRef = useRef(false)
  // date_modified means the user changed the completion date (picker edit or the
  // before-last-performed confirm) — opening the editor alone does not count. The ref is written
  // synchronously because the confirm handler submits before the state update re-renders.
  const [completedAtChanged, setCompletedAtChangedState] = useState(false)
  const completedAtChangedRef = useRef(false)
  const setCompletedAtChanged = (changed: boolean) => {
    completedAtChangedRef.current = changed
    setCompletedAtChangedState(changed)
  }

  // Kept fresh each render so the transform (which only re-registers on isInline/returnTo)
  // always reads the current site timezone without widening the effect dependencies.
  const siteTimeZoneRef = useRef(props.siteTimeZone)
  siteTimeZoneRef.current = props.siteTimeZone

  // The transform closure is only re-registered on [isInline, returnTo], so it would otherwise
  // capture a stale `props`. Read-only lot rows change via prop reloads (Change modal) without
  // touching those deps, so keep the current lot props in a ref for the transform to read fresh
  // (REBUILD-111).
  const lotPropsRef = useRef({ linkedLot: props.linkedLot, usedLots: props.usedLots })
  lotPropsRef.current = { linkedLot: props.linkedLot, usedLots: props.usedLots }

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ open: false })

  // REBUILD-531: loggers created inline via "Add new user". Appended to the picklist and the newly
  // created one is auto-selected so the task can be completed under them without a page reload.
  const [addedLoggers, setAddedLoggers] = useState<LoggerOption[]>([])

  const handleLoggerCreated = (logger: CreatedLogger) => {
    setAddedLoggers((previous) =>
      previous.some((existing) => existing.id === logger.id)
        ? previous
        : [...previous, { id: logger.id, label: logger.label }],
    )
    form.setData('labLoggerId', String(logger.id))
  }

  const existingAttachmentByField = useMemo(() => {
    const map: Record<
      number,
      { id: number; attachmentName: string | null; downloadHref: string | null }
    > = {}
    ;(props.existingTaskLog?.dataFieldAttachments ?? []).forEach((attachment) => {
      if (attachment.instrumentTaskDataFieldId != null) {
        map[attachment.instrumentTaskDataFieldId] = attachment
      }
    })
    return map
  }, [props.existingTaskLog])

  const handleDataFieldFileSelected = (fieldId: number, file: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result)
      const dataBase64 = result.includes(',') ? result.split(',')[1] : result
      setDataFieldFiles((prev) => ({
        ...prev,
        [fieldId]: { attachmentName: file.name, mimeType: file.type, dataBase64 },
      }))
      setRemovedAttachmentFieldIds((prev) => prev.filter((id) => id !== fieldId))
    }
    reader.readAsDataURL(file)
  }

  const handleDataFieldFileRemoved = (fieldId: number) => {
    setDataFieldFiles((prev) => ({ ...prev, [fieldId]: null }))
    setRemovedAttachmentFieldIds((prev) => (prev.includes(fieldId) ? prev : [...prev, fieldId]))
  }

  const formErr = form.errors as TaskLogFormFieldErrors
  const pageErr = pageErrors as TaskLogFormFieldErrors | undefined

  const lotEntriesError = firstError(formErr.lotEntries) ?? firstError(pageErr?.lotEntries)
  const completedAtError =
    firstError(formErr.completedAt) ??
    firstError(pageErr?.completedAt) ??
    firstError(
      (pageErrors as Record<string, unknown>)?.['completed_at'] as string | string[] | undefined,
    )
  const modReasonError = firstError(formErr.modReason) ?? firstError(pageErr?.modReason)

  useLayoutEffect(() => {
    form.transform((data: TaskLogFormData) => {
      const baseline = buildInitialForm(props)
      const attachments = attachmentStateRef.current
      const dataFieldFilePayload = Object.entries(attachments.dataFieldFiles)
        .filter(([, file]) => file != null)
        .map(([fieldId, file]) => ({
          instrumentTaskDataFieldId: Number(fieldId),
          ...(file as DataFieldFilePayload),
        }))
      const siteTimeZone = siteTimeZoneRef.current
      // E36-S3 wire format: submit the raw wall-clock string, unmodified. The backend
      // resolves the instrument's site zone and converts to UTC via
      // SiteTimeZone.wall_clock_to_utc — the frontend no longer does timezone math here.
      // When editing, send the user's typed value as-is. For the "now" default, emit the
      // current site-local wall clock (via toWallClockString),
      // or the browser-local now when no site zone is known.
      // An open but unchanged editor submits "now": its stale mount-time value would otherwise
      // trip the server's earlier-day date_modified fallback (date_modified_submission?).
      const isDateModified = completedAtChangedRef.current
      const effectiveCompletedAt =
        editingCompletedAtRef.current && isDateModified
          ? data.completedAt
          : siteTimeZone
            ? toWallClockString(new Date(), siteTimeZone)
            : toBrowserWallClockString()
      // In read-only lot-row mode the lot entries are a pure projection of the current prop
      // selections (the user changes them via the Change modal, not the form). A supply selected
      // AFTER the form mounted only updates props — the mount-time `data.lotEntries` snapshot is
      // stale — so recompute from live props here; otherwise the submit sends empty/stale entries
      // and the server rejects with RequiredLotMissingError (REBUILD-111). The legacy editable
      // dropdown keeps `data.lotEntries` as its user-driven source of truth.
      const effectiveLotEntries = usesReadOnlyLotRows(lotPropsRef.current)
        ? deriveReadOnlyLotEntries(lotPropsRef.current)
        : (data.lotEntries ?? baseline.lotEntries)
      // NPT (No Testing) records a non-performed task with no data-field values. Drop
      // empty-valued entries so a required field's blank {id, value:''} does not trip the
      // server's required-field validation (REBUILD-434); the server also ignores them for NPT.
      const submittedDataFieldEntries = data.dataFieldEntries ?? baseline.dataFieldEntries
      const dataFieldEntries = pendingIsNptRef.current
        ? submittedDataFieldEntries.filter(
            (entry) => entry.value.trim() !== '' || entry.linearAcceptable !== '',
          )
        : submittedDataFieldEntries
      return {
        ...(isInline ? { source: 'inline' } : {}),
        ...(returnTo ? { return_to: returnTo } : {}),
        taskLog: {
          ...baseline,
          ...data,
          completedAt: effectiveCompletedAt,
          comments: data.comments,
          dataFieldEntries,
          lotEntries: effectiveLotEntries,
          dataFieldFiles: dataFieldFilePayload,
          removedAttachmentFieldIds: attachments.removedAttachmentFieldIds,
          is_npt: pendingIsNptRef.current,
          entered_late: pendingEnteredLateRef.current,
          date_modified: isDateModified,
          mod_reason: isDateModified ? data.modReason : null,
        },
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInline, returnTo])

  const [editingCompletedAt, setEditingCompletedAt] = useState(false)
  const editingCompletedAtRef = useRef(false)
  editingCompletedAtRef.current = editingCompletedAt

  // Re-render while the non-editing "Completed at" label is visible so it tracks the moment
  // Mark as Complete will stamp (the transform reads the clock at submit), instead of freezing
  // at whatever time the form last re-rendered (REBUILD-610).
  const clockNow = useLiveClock(editingCompletedAt)

  // Non-editing "Completed at" mirrors submit: site-local now + zone abbr for that instant
  // (REBUILD-598). Edit mode keeps the DateTimePickerInput mask without a zone suffix.
  const recordedCompletedAtWallClock = props.siteTimeZone
    ? toWallClockString(clockNow, props.siteTimeZone)
    : toBrowserWallClockString(clockNow)
  const recordedCompletedAtDisplay = formatTenantDateTimeWithZone(
    recordedCompletedAtWallClock,
    siteZoneAbbreviation(recordedCompletedAtWallClock, props.siteTimeZone),
    tenantTimeFormat,
  )

  // Resolves the completed-at wall-clock input to a real instant for comparisons.
  // When the site timezone is known the input is site-local; otherwise browser-local.
  // Empty/blank input yields an Invalid Date so callers' Number.isNaN guards still hold.
  const completedAtInstant = (wallClock: string): Date =>
    props.siteTimeZone && wallClock
      ? new Date(wallClockToUtcIso(wallClock, props.siteTimeZone))
      : new Date(wallClock)

  // Late completions (REBUILD-401): an edited completed-at after the open-miss boundary is
  // treated the same as "Current time" — the picker caps at "now", not at the miss instant.
  // Upper-bound resolve blocking stays on NC Resolve paths.
  const notFutureMaxDate = props.siteTimeZone ? nowInTimeZone(props.siteTimeZone) : new Date()
  const completedAtMaxDate = notFutureMaxDate

  const executeSubmit = () => {
    setSubmittingAsNpt(pendingIsNptRef.current)
    const options = isInline
      ? {
          preserveScroll: true,
          ...(returnTo ? {} : { preserveState: true }),
          ...(reloadOnly ? { only: reloadOnly } : {}),
          onSuccess: () => {
            onSuccess?.()
          },
        }
      : undefined

    if (formMethod === 'post') {
      form.post(formAction, options)
    } else {
      form.patch(formAction, options)
    }
  }

  const validateRequiredFields = (): boolean => {
    form.clearErrors()
    const errors: Record<string, string> = {}
    const fieldList = props.dataFields ?? []
    const currentFiles = dataFieldFiles
    const existingAttachments = existingAttachmentByField

    for (const field of fieldList) {
      const fieldId = Number(field.id)
      const entry = form.data.dataFieldEntries.find(
        (e) => Number(e.instrumentTaskDataFieldId) === fieldId,
      )

      // Numeric fields (number, and linear when a value is typed) must hold a valid number.
      // Mirrors the legacy "onlyNumbers" input restriction so a non-numeric value is caught
      // with a clear, field-level message instead of a generic server error.
      const isNumericField = field.dataFieldType === 'number' || field.dataFieldType === 'linear'
      const numericValue = entry?.value?.trim()
      if (isNumericField && numericValue && !isAllowedNumericInput(numericValue)) {
        errors[`dataField${fieldId}`] = t('instruments.taskLogs.numericFieldInvalid')
        continue
      }

      if (!field.required) continue

      if (field.dataFieldType === 'fileAttachment') {
        const hasNewFile = Boolean(currentFiles[fieldId])
        const hasExisting =
          Boolean(existingAttachments[fieldId]) && !removedAttachmentFieldIds.includes(fieldId)
        if (!hasNewFile && !hasExisting) {
          errors[`dataField${fieldId}`] = t('instruments.taskLogs.fieldRequired')
        }
      } else if (field.dataFieldType === 'defined') {
        if (!entry?.value) {
          errors[`dataField${fieldId}`] = t('instruments.taskLogs.fieldRequired')
        }
      } else if (field.dataFieldType === 'linear') {
        const isAcceptable = entry?.linearAcceptable === LINEAR_ACCEPTABLE
        if (!isAcceptable && !entry?.value?.trim()) {
          errors[`dataField${fieldId}`] = t('instruments.taskLogs.fieldRequired')
        }
      } else if (!entry?.value?.trim()) {
        errors[`dataField${fieldId}`] = t('instruments.taskLogs.fieldRequired')
      }
    }

    if (Object.keys(errors).length > 0) {
      form.setError(errors as Record<string, string>)
      return false
    }

    // Validate completed_at is not blank when the user edited the date picker
    if (editingCompletedAtRef.current && !form.data.completedAt.trim()) {
      form.setError({ completedAt: t('instruments.taskLogs.completedAtRequired') } as Record<
        string,
        string
      >)
      return false
    }

    // Validate completed_at is not in the future (when user manually edited the date)
    if (editingCompletedAtRef.current && form.data.completedAt) {
      const completedAt = completedAtInstant(form.data.completedAt)
      if (!Number.isNaN(completedAt.getTime()) && completedAt > new Date()) {
        form.setError({ completedAt: t('instruments.taskLogs.futureDateError') } as Record<
          string,
          string
        >)
        return false
      }
      // REBUILD-627: wall-clock strings are YYYY-MM-DDTHH:mm, so a lexical compare is enough.
      if (form.data.completedAt < MIN_COMPLETED_AT_WALL_CLOCK) {
        form.setError({ completedAt: t('instruments.taskLogs.tooEarlyDateError') } as Record<
          string,
          string
        >)
        return false
      }
    }

    if (!validateModReason()) return false

    return true
  }

  // Shared by validateRequiredFields and handleNptSubmit — No Testing skips the full validation.
  const validateModReason = (): boolean => {
    if (!completedAtChangedRef.current) return true
    if (form.data.modReason.replace(/<[^>]*>/g, '').trim()) return true
    form.setError({ modReason: t('instruments.taskLogs.modReasonRequired') } as Record<
      string,
      string
    >)
    return false
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)
    pendingIsNptRef.current = false
    pendingEnteredLateRef.current = false
    // Not editing the completed-at means the submit uses "now", so it is never date-modified.
    if (!editingCompletedAtRef.current) {
      setCompletedAtChanged(false)
    }

    if (!validateRequiredFields()) return

    // Legacy parity (checkCommentRequired): an unacceptable measurement forces a comment.
    if (commentRequired && commentIsEmpty) return

    // Entered completed-at is before last performed → guard. Checked first so it stays
    // reachable for overdue tasks (which would otherwise always hit the Late dialog).
    const lastCompletedAt = complianceWindow?.lastCompletedAt
    if (lastCompletedAt) {
      const completedAt = completedAtInstant(form.data.completedAt)
      const lastCompleted = props.siteTimeZone
        ? new Date(wallClockToUtcIso(lastCompletedAt.slice(0, 16), props.siteTimeZone))
        : new Date(lastCompletedAt)
      if (!Number.isNaN(completedAt.getTime()) && completedAt < lastCompleted) {
        const lastPerformedDate = formatSiteDateTime(
          lastCompletedAt,
          props.siteTimeZone,
          tenantTimeFormat,
        )
        setConfirmDialog({
          open: true,
          type: 'before_last_performed',
          message: t('instruments.taskLogs.beforeLastPerformedMessage').replace(
            '{{lastPerformedDate}}',
            lastPerformedDate,
          ),
        })
        return
      }
    }

    // Legacy parity: show "Late" dialog only when the task's current status is Overdue (ODE).
    // The backend status engine has already determined whether the task is overdue.
    const isCurrentlyOverdue = complianceWindow?.taskStatusCode === 'ODE'
    if (isCurrentlyOverdue) {
      setConfirmDialog({
        open: true,
        type: 'late',
        message: t('instruments.taskLogs.lateConfirmMessage'),
      })
      return
    }

    executeSubmit()
  }

  // Confirm a pending submit. Only the Late confirmation marks the log entered_late; the
  // before-last-performed guard is a plain "are you sure?" and must not flag the log as Late.
  const handleConfirmDialog = () => {
    if (confirmDialog.open && confirmDialog.type === 'late') {
      pendingEnteredLateRef.current = true
    }
    if (confirmDialog.open && confirmDialog.type === 'before_last_performed') {
      setCompletedAtChanged(true)
    }
    setConfirmDialog({ open: false })
    executeSubmit()
  }

  const handleNptSubmit = () => {
    if (!validateModReason()) return
    pendingIsNptRef.current = true
    pendingEnteredLateRef.current = false
    setConfirmDialog({ open: false })
    executeSubmit()
  }

  const addLotRow = () => {
    form.setData('lotEntries', [...form.data.lotEntries, { lotId: '', lotDetailId: '' }])
  }

  const removeLotRow = (index: number) => {
    const next = form.data.lotEntries.filter((_row, rowIndex) => rowIndex !== index)
    form.setData('lotEntries', next.length ? next : [{ lotId: '', lotDetailId: '' }])
  }

  const updateLotRow = (index: number, patch: Partial<{ lotId: string; lotDetailId: string }>) => {
    const next = form.data.lotEntries.map((row, i) => (i === index ? { ...row, ...patch } : row))
    if (patch.lotId !== undefined) {
      next[index] = { ...next[index], lotDetailId: '' }
    }
    form.setData('lotEntries', next)
  }

  /** Whether to show the legacy-style read-only lot rows (new design) or the dropdown UI (legacy standalone page). */
  const useLotRowsDesign = linkedLot != null || usedLots.length > 0

  const updateDataFieldValue = (fieldId: number, value: string) => {
    const next = form.data.dataFieldEntries.map((row) =>
      row.instrumentTaskDataFieldId === fieldId ? { ...row, value } : row,
    )
    form.setData('dataFieldEntries', next)
  }

  const updateLinearAcceptable = (fieldId: number, choice: string) => {
    const next = form.data.dataFieldEntries.map((row) =>
      row.instrumentTaskDataFieldId === fieldId
        ? { ...row, linearAcceptable: choice, value: choice === LINEAR_ACCEPTABLE ? '' : row.value }
        : row,
    )
    form.setData('dataFieldEntries', next)
  }

  const hasInvalidLots = lotValidityWarnings.length > 0
  const commentIsEmpty = richTextToPlainText(form.data.comments).length === 0
  const commentMissing = commentRequired && commentIsEmpty
  const showDayOffNotice = Boolean(
    props.dayOff && props.scheduleLevel && complianceWindow?.lastCompletedAt,
  )

  // Base picklist plus any inline-created loggers (REBUILD-531), de-duplicated by id so a logger
  // created inline is not shown twice if a prop reload later brings it in.
  const mergedLoggerOptions = useMemo(() => {
    const base = loggerOptions ?? []
    const baseIds = new Set(base.map((option) => option.id))
    const extras = addedLoggers.filter((option) => !baseIds.has(option.id))
    return [...base, ...extras]
  }, [loggerOptions, addedLoggers])

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {pageErr?.base && !lotEntriesError && !completedAtError && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {(Array.isArray(pageErr.base) ? pageErr.base : [pageErr.base]).join(' ')}
        </div>
      )}

      {/* Lot validity warning — blocks Mark-as-Complete until resolved */}
      {hasInvalidLots && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 space-y-2"
          role="alert"
        >
          <p className="text-sm font-semibold text-destructive">
            {t('instruments.taskLogs.lotInvalidWarningTitle')}
          </p>
          <ul className="text-sm text-destructive space-y-0.5">
            {lotValidityWarnings.map((warning, i) => (
              <li key={i}>{lotWarningLabel(warning)}</li>
            ))}
          </ul>
          <div className="flex items-center gap-3 pt-1">
            <p className="text-xs text-destructive">{t('instruments.taskLogs.lotInvalidHint')}</p>
            {changeLotSlot}
          </div>
        </div>
      )}

      {/* Compliance timeline — suppressed in inline mode because the accordion already shows it
          above; also hidden until first completion (legacy: no timeline without lastPerformed). */}
      {complianceWindow?.lastCompletedAt && !isInline && (
        <ComplianceTimeline window={complianceWindow} siteTimeZone={props.siteTimeZone} />
      )}

      {showDayOffNotice && (
        <Alert role="status" data-testid="day-off-notice">
          <AlertDescription className="space-y-1">
            <p>{t('instruments.taskLogs.dayOffTitle', { schedule: props.scheduleLevel ?? '' })}</p>
            <p>{t('instruments.taskLogs.dayOffMaintenance')}</p>
            <p>
              {t('instruments.taskLogs.dayOffLastPerformed', {
                lastPerformedDate: formatSiteDateTime(
                  complianceWindow?.lastCompletedAt ?? '',
                  props.siteTimeZone,
                  tenantTimeFormat,
                ),
              })}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* 1. Lots */}
      {(useLotRowsDesign || (lotOptions ?? []).length > 0 || linkedLotNoInventory) && (
        <section className="space-y-3 rounded-lg border p-3">
          <h2 className="text-sm font-semibold">{t('instruments.taskLogs.sectionLots')}</h2>

          {lotEntriesError && (
            <p className="text-destructive text-sm" role="alert">
              {lotEntriesError}
            </p>
          )}

          {useLotRowsDesign ? (
            // Legacy-style read-only rows: Lots Linked + Lots Used
            <div className="space-y-3">
              {linkedLot != null && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('instruments.taskLogs.lotsLinked')}
                  </p>
                  <LotReadOnlyRow
                    lot={linkedLot}
                    changeSlot={lotModalSlots?.get(linkedLot.id)}
                    siteTimeZone={props.siteTimeZone}
                  />
                </div>
              )}
              {usedLots.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('instruments.taskLogs.lotsUsed')}
                  </p>
                  <div className="space-y-2">
                    {usedLots.map((usedLot) => (
                      <LotReadOnlyRow
                        key={usedLot.id}
                        lot={usedLot}
                        changeSlot={lotModalSlots?.get(usedLot.id)}
                        siteTimeZone={props.siteTimeZone}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Legacy standalone Log page: editable dropdown rows
            <>
              {linkedLotNoInventory && (
                <p className="text-sm text-muted-foreground">
                  {t('instruments.taskLogs.linkedLotNoInventory')}
                </p>
              )}
              <div className="flex justify-end">
                {canSubmitTaskLog && (
                  <Button type="button" variant="outline" size="sm" onClick={addLotRow}>
                    {t('instruments.taskLogs.addLotRow')}
                  </Button>
                )}
              </div>
              {form.data.lotEntries.map((row, index) => {
                const lotMeta = (lotOptions ?? []).find(
                  (lotOption) => String(lotOption.lotId) === row.lotId,
                )
                const details = lotMeta?.lotDetails ?? []
                return (
                  <div
                    key={`lot-${index}-${row.lotId}`}
                    className="grid gap-3 rounded-md border border-dashed p-3 sm:grid-cols-2"
                  >
                    <div className="space-y-1.5">
                      <Label>{t('instruments.taskLogs.lot')}</Label>
                      <Select
                        value={row.lotId || undefined}
                        onValueChange={(selectedValue) =>
                          updateLotRow(index, { lotId: selectedValue })
                        }
                      >
                        <SelectTrigger className={cn(lotEntriesError && 'border-destructive')}>
                          <SelectValue placeholder={t('instruments.taskLogs.selectLot')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(lotOptions ?? []).map((lotOption) => (
                            <SelectItem key={lotOption.lotId} value={String(lotOption.lotId)}>
                              {lotOption.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('instruments.taskLogs.lotDetail')}</Label>
                      <Select
                        value={row.lotDetailId || undefined}
                        onValueChange={(selectedValue) =>
                          updateLotRow(index, { lotDetailId: selectedValue })
                        }
                        disabled={!row.lotId}
                      >
                        <SelectTrigger className={cn(lotEntriesError && 'border-destructive')}>
                          <SelectValue placeholder={t('instruments.taskLogs.selectLotDetail')} />
                        </SelectTrigger>
                        <SelectContent>
                          {details.map((lotDetail) => (
                            <SelectItem key={lotDetail.id} value={String(lotDetail.id)}>
                              {lotDetail.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {canSubmitTaskLog && form.data.lotEntries.length > 1 && (
                      <div className="sm:col-span-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLotRow(index)}
                        >
                          {t('instruments.taskLogs.removeLotRow')}
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </section>
      )}

      {/* 1b. Task notes — legacy rendered the Notes box right after Lots Used */}
      {notesSlot}

      {/* 2. Data fields */}
      {(dataFields ?? []).length > 0 && (
        <section className="space-y-3 rounded-lg border p-3">
          <DataFieldInputs
            showContainer={false}
            showSizeHint
            dataFields={dataFields ?? []}
            entries={form.data.dataFieldEntries}
            errors={{ ...pageErrors, ...form.errors }}
            onUpdateValue={updateDataFieldValue}
            onUpdateLinearAcceptable={updateLinearAcceptable}
            onValidityChange={setDataFieldsValid}
            onCommentRequiredChange={setCommentRequired}
            dataFieldFiles={dataFieldFiles}
            onFileSelected={handleDataFieldFileSelected}
            onFileRemoved={handleDataFieldFileRemoved}
            existingAttachmentByField={existingAttachmentByField}
            removedAttachmentFieldIds={removedAttachmentFieldIds}
          />
        </section>
      )}

      {/* 3. Performed by + Date/Time */}
      <section className="space-y-3 rounded-lg border p-3">
        <h2 className="text-sm font-semibold">{t('instruments.taskLogs.sectionLogger')}</h2>
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[180px] space-y-1.5">
            {isPrMode ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t('instruments.taskLogs.completedByLabel')}
                </p>
                <p className="text-sm font-medium">
                  {currentUserInfo?.displayName ?? t('instruments.taskLogs.completedByCurrentUser')}
                </p>
              </>
            ) : (
              <>
                {mergedLoggerOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('instruments.taskLogs.noLoggers')}
                  </p>
                ) : (
                  <>
                    <Label htmlFor="labLoggerId">{t('instruments.taskLogs.logger')}</Label>
                    <SearchableSelect
                      id="labLoggerId"
                      value={form.data.labLoggerId || ''}
                      onValueChange={(selectedValue) => form.setData('labLoggerId', selectedValue)}
                      placeholder={t('instruments.taskLogs.selectLogger')}
                      options={mergedLoggerOptions.map((loggerOption) => ({
                        value: String(loggerOption.id),
                        label: loggerOption.label,
                      }))}
                    />
                  </>
                )}
                {/* REBUILD-531: inline "Add new user" — shown only under open logging, where the
                    picker is offered. The server (Web::LoggerPolicy) enforces the same guard. */}
                {canSubmitTaskLog && (
                  <AddLoggerInline
                    instrumentId={props.instrumentId}
                    onCreated={handleLoggerCreated}
                  />
                )}
              </>
            )}
          </div>
          <div className="flex-1 min-w-[200px] space-y-1.5">
            {editingCompletedAt ? (
              <div className="space-y-1.5">
                <Label htmlFor="completedAt">{t('instruments.taskLogs.completedAt')}</Label>
                <DateTimePickerInput
                  id="completedAt"
                  value={form.data.completedAt}
                  onChange={(val) => {
                    if (val !== form.data.completedAt) setCompletedAtChanged(true)
                    form.setData('completedAt', val)
                  }}
                  maxDate={completedAtMaxDate}
                  timeZone={props.siteTimeZone ?? undefined}
                  className="max-w-[15rem]"
                  inputClassName={cn(completedAtError && 'border-destructive')}
                  aria-invalid={Boolean(completedAtError)}
                  showNowButton={false}
                />
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={() => {
                    setEditingCompletedAt(false)
                    setCompletedAtChanged(false)
                  }}
                >
                  ({t('instruments.taskLogs.completedAtReset')})
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">
                  {t('instruments.taskLogs.completedAt')}:
                </span>
                <span className="font-medium">{recordedCompletedAtDisplay}</span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={() => setEditingCompletedAt(true)}
                >
                  ({t('instruments.taskLogs.completedAtEdit')})
                </Button>
              </div>
            )}
            {completedAtError && <p className="text-destructive text-sm">{completedAtError}</p>}
          </div>
        </div>
      </section>

      {/* 4. Notes */}
      <section className="space-y-3 rounded-lg border p-3">
        <h2 className="text-sm font-semibold">{t('instruments.taskLogs.sectionNotes')}</h2>
        <div className="space-y-1.5">
          <Label htmlFor="comments">
            {t('instruments.taskLogs.comments')}
            {commentRequired && ` (${t('instruments.taskLogs.required')})`}
          </Label>
          <RichTextEditor
            value={form.data.comments}
            onChange={(val) => form.setData('comments', val)}
            placeholder={t('instruments.taskLogs.commentsPlaceholder')}
            className={cn(submitAttempted && commentMissing && 'border-destructive')}
          />
          {commentRequired && (
            <p className="text-muted-foreground text-xs">
              {t('instruments.taskLogs.commentRequiredHint')}
            </p>
          )}
          {submitAttempted && commentMissing && (
            <p className="text-destructive text-sm">
              {t('instruments.taskLogs.commentRequiredError')}
            </p>
          )}
        </div>
        {completedAtChanged && (
          <div className="space-y-1.5">
            <Label htmlFor="modReason">
              {t('instruments.taskLogs.modReasonLabel')} ({t('instruments.taskLogs.required')})
            </Label>
            <RichTextEditor
              value={form.data.modReason}
              onChange={(val) => form.setData('modReason', val)}
              placeholder={t('instruments.taskLogs.modReasonPlaceholder')}
              className={cn(modReasonError && 'border-destructive')}
            />
            {modReasonError && <p className="text-destructive text-sm">{modReasonError}</p>}
          </div>
        )}
      </section>

      {!canSubmitTaskLog && <NoAccessNotice />}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {canSubmitTaskLog && (
          <>
            <Button
              type="submit"
              disabled={form.processing || hasInvalidLots || !dataFieldsValid || commentMissing}
            >
              {form.processing && !submittingAsNpt
                ? t('instruments.taskLogs.submitting')
                : isInline
                  ? t('instruments.taskLogs.markComplete')
                  : t('instruments.taskLogs.submit')}
            </Button>
            {/* NPT is always available regardless of lot validity — task is not being performed */}
            <Button
              type="button"
              variant="outline"
              disabled={form.processing}
              onClick={handleNptSubmit}
            >
              {form.processing && submittingAsNpt
                ? t('instruments.taskLogs.noTestingSubmitting')
                : t('instruments.taskLogs.noTesting')}
            </Button>
          </>
        )}
        {isInline ? (
          <Button type="button" variant="ghost" disabled={form.processing} onClick={onCancel}>
            {t('instruments.taskLogs.cancel')}
          </Button>
        ) : null}
      </div>

      {(form.hasErrors ||
        (pageErr &&
          Object.keys(pageErr).filter((k) => k !== 'completed_at' && k !== 'completedAt').length >
            0)) && (
        <p className="text-sm text-destructive">{t('instruments.taskLogs.checkRequiredFields')}</p>
      )}

      {/* Overdue-Late / Before-last-performed confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) setConfirmDialog({ open: false })
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.open && confirmDialog.type === 'late'
                ? t('instruments.taskLogs.lateConfirmTitle')
                : t('instruments.taskLogs.beforeLastPerformedTitle')}
            </DialogTitle>
            <DialogDescription>{confirmDialog.open ? confirmDialog.message : ''}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={form.processing}
              onClick={() => setConfirmDialog({ open: false })}
            >
              {t('instruments.taskLogs.lateConfirmCancel')}
            </Button>
            <Button type="button" disabled={form.processing} onClick={handleConfirmDialog}>
              {confirmDialog.open && confirmDialog.type === 'before_last_performed'
                ? t('instruments.taskLogs.beforeLastPerformedConfirm')
                : t('instruments.taskLogs.lateConfirmConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

export default TaskLogForm
